import { IYearMonth } from '@/coreDomainsModule/date';
import {
  AddColumnUseAmountType,
  ColumnNamesOfExportFile,
  IAddColumnUseAmount,
  isSubTotalSupplierForecastExportData,
  ISubTotalSupplierForecastExportData,
  ISupplierForecastExportBaseData,
  ISupplierForecastExportData,
  SalesResultExportData,
  SubTotalAmountInfo,
  SupplierAnnounceExportData,
  SupplierForecastExportData,
} from '../entities/supplierForecastExportData';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { i18n } from '@/i18n';

/**
 * ISupplierForecastExportData関連のロジックをまとめる
 * TODO: serviceに書くのはあってないかも
 */
export class SupplierForecastExportDataService {
  private static t = i18n.t;
  /**
   * 追加する列の情報を取得する
   * @param startYearMonth - 開始年月
   * @param endYearMonth - 終了年月
   */
  public static getAddingColumnsUseAmount = (startYearMonth: IYearMonth, endYearMonth: IYearMonth): IAddColumnUseAmount[] => {
    const addingColumnsUseAmount: IAddColumnUseAmount[] = [];

    const monthCount = DateService.calculateDiffMonths(
      DateService.convertIDateToDate({ ...startYearMonth, day: 1 }),
      DateService.convertIDateToDate({ ...endYearMonth, day: 1 })
    );
    for (let i = 0; i <= monthCount; i++) {
      const targetYearMonth = DateService.updateYearMonth(startYearMonth.year, startYearMonth.month, i);
      addingColumnsUseAmount.push({
        type: 'supplierForecast',
        yearMonth: {
          year: targetYearMonth.updateYear,
          month: targetYearMonth.updateMonth,
        },
        amount: 0,
      });
      // 開始年月のみ、内示の次に販売実績を追加する
      if (i === 0) {
        addingColumnsUseAmount.push({
          type: 'salesResult',
          yearMonth: {
            year: targetYearMonth.updateYear,
            month: targetYearMonth.updateMonth,
          },
          amount: 0,
        });
      }
    }
    return addingColumnsUseAmount;
  };

  /**
   * 追加する列の名前を取得する
   * @param type - 列のタイプ
   */
  public static getAddingColumnName = (type: AddColumnUseAmountType): string => {
    switch (type) {
      case 'supplierForecast':
        return SupplierForecastExportDataService.t('内示');
      case 'salesResult':
        return SupplierForecastExportDataService.t('販売実績');
    }
  };

  /**
   * 出力する値を取得する
   * @param business - 取引情報
   * @param columnName - エクセルの列名
   * @param targetYearMonth - 対象の年月
   */
  public static getExportValue = (
    business: ISupplierForecastExportData,
    columnName: ColumnNamesOfExportFile,
    targetYearMonth?: IYearMonth
  ): string | number => {
    switch (columnName) {
      case '仕入先CD':
        return business.supplierNumber;
      case '仕入先名':
        return business.supplierName;
      case '商品CD':
        return business.businessNumber;
      case '商品名':
        return business.businessName;
      case '置場CD':
        return business.warehouseNumber;
      case '置場名':
        return business.warehouse;
      case 'memo1':
        return business.memo1 || '';
      case 'memo2':
        return business.memo2 || '';
      case '容量':
        return business.package;
      case '内示':
        return targetYearMonth
          ? SupplierForecastExportDataService.getSupplierForecastValue(business.SupplierForecasts, targetYearMonth)
          : 0;
      case '販売実績':
        return targetYearMonth && business.SalesResult
          ? SupplierForecastExportDataService.getSalesResultValue(business.SalesResult, targetYearMonth)
          : 0;
    }
  };

  /**
   * 合計行の出力する値を取得する
   * @param subTotalData - 合計行の情報
   * @param columnName - エクセルの列名
   * @param targetYearMonth - 対象の年月
   */
  public static getExportValueSubTotal = (
    subTotalData: ISubTotalSupplierForecastExportData,
    columnName: ColumnNamesOfExportFile,
    targetYearMonth?: IYearMonth
  ): string | number => {
    switch (columnName) {
      case '仕入先CD':
        return subTotalData.supplierNumber;
      case '仕入先名':
        return subTotalData.supplierName;
      case '商品CD':
        return subTotalData.businessNumber;
      case '商品名':
        return subTotalData.businessName;
      case '置場CD':
        return '';
      case '置場名':
        return '';
      case 'memo1':
        return '';
      case 'memo2':
        return '';
      case '容量':
        return '合計';
      case '内示':
        return targetYearMonth
          ? SupplierForecastExportDataService.getAmountValueSubTotal(subTotalData.sumSupplierForecasts, targetYearMonth)
          : 0;
      case '販売実績':
        return targetYearMonth && subTotalData.sumSalesResult
          ? SupplierForecastExportDataService.getAmountValueSubTotal([subTotalData.sumSalesResult], targetYearMonth)
          : 0;
      default:
        return '';
    }
  };

  /**
   * 総計として出力する値を取得する
   * @param exportData - 出力用の情報
   * @param columnName - エクセルの列名
   * @param targetYearMonth - 対象の年月
   */
  public static getExportValueAllTotal = (
    exportData: SupplierForecastExportData[],
    columnName: ColumnNamesOfExportFile,
    targetYearMonth?: IYearMonth
  ): string | number => {
    switch (columnName) {
      case '容量':
        return '総計';
      case '内示':
        return targetYearMonth ? this.calculateSupplierForecastAllTotal(exportData, targetYearMonth) : 0;
      case '販売実績':
        return targetYearMonth ? this.calculateSalesResultAllTotal(exportData, targetYearMonth) : 0;
      default:
        return '';
    }
  };

  /**
   * 仕入先内示の値を取得する
   * @param supplierForecasts - 仕入先内示の情報
   * @param targetYearMonth - 対象の年月
   */
  public static getSupplierForecastValue = (
    supplierForecasts: SupplierAnnounceExportData[],
    targetYearMonth: IYearMonth
  ): string | number => {
    const targetSupplierForecast = supplierForecasts.find(
      (supplierForecast) =>
        supplierForecast.supplierForecastTargetYear === targetYearMonth.year &&
        supplierForecast.supplierForecastTargetMonth === targetYearMonth.month
    );
    return targetSupplierForecast ? targetSupplierForecast.supplierForecastAmount : '';
  };

  /**
   * 販売実績の値を取得する
   * @param salesResult - 販売実績の情報
   * @param targetYearMonth - 対象の年月
   */
  public static getSalesResultValue = (salesResult: SalesResultExportData, targetYearMonth: IYearMonth): string | number => {
    const isMatchedYearMonth =
      salesResult.salesResultTargetYear === targetYearMonth.year && salesResult.salesResultTargetMonth === targetYearMonth.month;
    return isMatchedYearMonth ? salesResult.salesResultAmountTotal : '';
  };

  /**
   * 合計行の数量値を取得する
   * @param amountInfo - 合計行の情報
   * @param targetYearMonth - 対象の年月
   */
  public static getAmountValueSubTotal = (amountInfo: SubTotalAmountInfo[], targetYearMonth: IYearMonth): string | number => {
    const targetSupplierForecast = amountInfo.find(
      (supplierForecast) => supplierForecast.targetYear === targetYearMonth.year && supplierForecast.targetMonth === targetYearMonth.month
    );
    return targetSupplierForecast ? targetSupplierForecast.amountTotal : '';
  };

  /**
   * 2つのデータが同じ基本データかどうかを判定する
   * @param data1 - データ1
   * @param data2 - データ2
   */
  public static isSameBaseData = (data1: ISupplierForecastExportBaseData, data2: ISupplierForecastExportBaseData): boolean => {
    if (!data2) return false;
    // 仕入先CDと商品CDが同じものをグループとして扱う
    return data1.supplierNumber === data2.supplierNumber && data1.businessNumber === data2.businessNumber;
  };

  /**
   * 合計行を作成する
   * @param targetData - 対象のデータ
   * @param targetStartYearMonth - 対象の開始年月
   * @param targetEndYearMonth - 対象の終了年月
   */
  public static createSubTotalSupplierForecastExportData = (
    targetData: ISupplierForecastExportData[],
    targetStartYearMonth: IYearMonth,
    targetEndYearMonth: IYearMonth
  ) => {
    // 販売実績の合計を取得
    const salesResults = targetData
      .map((business) => business.SalesResult)
      .filter((result): result is SalesResultExportData => result !== undefined && result !== null);
    const subTotalSalesResult = SupplierForecastExportDataService.getSubTotalSalesResult(salesResults);
    // 仕入先内示の合計を取得
    const supplierForecasts = targetData.map((business) => business.SupplierForecasts).flat();
    const subTotalSupplierForecasts = SupplierForecastExportDataService.getSubTotalSupplierForecastExportData(
      supplierForecasts,
      targetStartYearMonth,
      targetEndYearMonth
    );
    // 合計行を作成して追加
    const subTotal: ISubTotalSupplierForecastExportData = {
      ...targetData[0],
      sumSalesResult: subTotalSalesResult,
      sumSupplierForecasts: subTotalSupplierForecasts,
    };
    return subTotal;
  };

  /**
   * 合計行の販売実績情報を取得する
   * @param salesResults - 販売実績の情報
   */
  public static getSubTotalSalesResult = (salesResults: SalesResultExportData[]): SubTotalAmountInfo | undefined => {
    if (salesResults.length === 0) return undefined;

    return {
      targetYear: salesResults[0].salesResultTargetYear,
      targetMonth: salesResults[0].salesResultTargetMonth,
      amountTotal: salesResults.reduce((prev, current) => prev + current.salesResultAmountTotal, 0),
    };
  };

  /**
   * 合計行の仕入先内示情報を取得する
   * @param supplierForecasts - 仕入先内示の情報
   * @param targetStartYearMonth - 対象の開始年月
   * @param targetEndYearMonth - 対象の終了年月
   */
  public static getSubTotalSupplierForecastExportData = (
    supplierForecasts: SupplierAnnounceExportData[],
    targetStartYearMonth: IYearMonth,
    targetEndYearMonth: IYearMonth
  ): SubTotalAmountInfo[] => {
    const result: SubTotalAmountInfo[] = [];
    if (supplierForecasts.length === 0) return result;

    const monthCount = DateService.calculateDiffMonths(
      DateService.convertIDateToDate({ ...targetStartYearMonth, day: 1 }),
      DateService.convertIDateToDate({ ...targetEndYearMonth, day: 1 })
    );
    for (let i = 0; i <= monthCount; i++) {
      // 対象の年月ごとに合計を作成する
      const { updateYear: targetYear, updateMonth: targetMonth } = DateService.updateYearMonth(
        targetStartYearMonth.year,
        targetStartYearMonth.month,
        i
      );
      // 対象年月が一致する仕入先内示で絞り込む
      const targetSupplierForecasts = supplierForecasts.filter(
        (supplierForecast) =>
          supplierForecast.supplierForecastTargetYear === targetYear && supplierForecast.supplierForecastTargetMonth === targetMonth
      );
      if (targetSupplierForecasts.length !== 0) {
        result.push({
          targetYear,
          targetMonth,
          amountTotal: targetSupplierForecasts.reduce((prev, current) => prev + current.supplierForecastAmount, 0),
        });
      }
    }
    return result;
  };

  /**
   * 仕入先内示の総計を計算する
   * @param exportData - 出力用の情報
   * @param targetYearMonth - 対象の年月
   */
  public static calculateSupplierForecastAllTotal = (exportData: SupplierForecastExportData[], targetYearMonth: IYearMonth): number => {
    // 合計行の総和を計算する
    return exportData
      .filter((data): data is ISupplierForecastExportData => !isSubTotalSupplierForecastExportData(data))
      .reduce((prev, current) => {
        return (
          prev +
          current.SupplierForecasts.filter(
            (supplierForecast) =>
              supplierForecast.supplierForecastTargetYear === targetYearMonth.year &&
              supplierForecast.supplierForecastTargetMonth === targetYearMonth.month
          ).reduce((prev, current) => prev + current.supplierForecastAmount, 0)
        );
      }, 0);
  };

  /**
   * 販売実績の総計を計算する
   * @param exportData - 出力用の情報
   * @param targetYearMonth - 対象の年月
   */
  public static calculateSalesResultAllTotal = (exportData: SupplierForecastExportData[], targetYearMonth: IYearMonth): number => {
    // 合計行の総和を計算する
    return exportData
      .filter((data): data is ISupplierForecastExportData => !isSubTotalSupplierForecastExportData(data))
      .reduce((prev, current) => {
        const value =
          current.SalesResult?.salesResultAmountTotal !== undefined &&
          current.SalesResult.salesResultTargetYear === targetYearMonth.year &&
          current.SalesResult.salesResultTargetMonth === targetYearMonth.month
            ? current.SalesResult.salesResultAmountTotal
            : 0;
        return prev + value;
      }, 0);
  };
}
